import { useCallback, useMemo } from "react";

import { SectionType } from "components/server-types";
import { selectArchivedSectionIds, termSelectors } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { ModelType } from "utils/type-utils";

export const useIsArchived = () => {
  const archivedSectionIds = useListSelector(selectArchivedSectionIds);
  const archivedSectionIdSet = useMemo(() => new Set(archivedSectionIds), [archivedSectionIds]);
  // Terms still needed for canUnarchive (own archived_at vs inherited from term).
  const terms = useListSelector(termSelectors.selectAll, ["id", "archived_at"]);

  const isArchived = useCallback(
    (section: ModelType) => archivedSectionIdSet.has(section.id),
    [archivedSectionIdSet],
  );

  const isActive = useCallback((section: ModelType) => !isArchived(section), [isArchived]);

  const canUnarchive = useCallback(
    (section: Pick<SectionType, "term_id" | "template_id" | "archived_at">) => {
      if (!section.archived_at) return false;
      const term = terms.find((t) => t.id === section.term_id);
      const templateArchived =
        section.template_id != null && archivedSectionIdSet.has(section.template_id);
      return Boolean(!term?.archived_at && !templateArchived);
    },
    [archivedSectionIdSet, terms],
  );

  return { isArchived, isActive, canUnarchive };
};
